import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Facebook, Instagram, Linkedin, MapPin, ExternalLink, CheckCircle2, Globe, Twitter } from 'lucide-react';

const GUIDES = {
  facebook: {
    icon: Facebook, color: '#1877F2', label: 'Facebook Business Page',
    heroText: 'Post your content on Facebook for maximum visibility.',
    setup: [
      { title: 'Create a Business Page', desc: 'Go to facebook.com/pages/create. Choose "Business or brand."' },
      { title: 'Fill in your info', desc: 'Add your business name, category (e.g. "Plumber"), address, phone, and website.' },
      { title: 'Upload a profile photo', desc: 'Use your logo or a professional photo of yourself/your team.' },
      { title: 'Add a cover photo', desc: 'Use a photo of your work, your truck, or your storefront.' },
      { title: 'Write a bio', desc: 'Describe your services and service area in 1-2 sentences.' },
    ],
    posting: [
      'Go to your Business Page',
      'Click "Create Post" at the top',
      'Paste the content from GetChosenFirst',
      'Add a photo if you have one (optional but recommended)',
      'Click "Post"',
    ],
    tips: [
      'Post 2-3 times per week for best results',
      'Photos get 2x more engagement than text-only posts',
      'Respond to comments within 24 hours',
      'Share customer success stories (with permission)',
    ],
  },
  instagram: {
    icon: Instagram, color: '#E1306C', label: 'Instagram Business Account',
    heroText: 'Instagram posts build visual credibility for AI.',
    setup: [
      { title: 'Download Instagram', desc: 'Get the Instagram app from the App Store or Google Play.' },
      { title: 'Create or switch to a Business account', desc: 'Go to Settings > Account > Switch to Professional Account > Business.' },
      { title: 'Fill in your profile', desc: 'Add your business name, bio (describe what you do + your city), and website link.' },
      { title: 'Upload a profile photo', desc: 'Use your logo or a professional headshot.' },
    ],
    posting: [
      'Open the Instagram app',
      'Tap the + button at the bottom',
      'Choose a photo (project photo, your team, your workspace)',
      'Paste the caption from GetChosenFirst',
      'Add relevant hashtags (we can suggest some)',
      'Tap "Share"',
    ],
    tips: [
      'Instagram is all about photos — always include an image',
      'Use 5-10 relevant hashtags (e.g. #DenverPlumber #PlumbingRepair)',
      'Post photos of your completed work',
      'Share before/after transformations — they perform great',
    ],
  },
  linkedin: {
    icon: Linkedin, color: '#0A66C2', label: 'LinkedIn Profile',
    heroText: 'LinkedIn boosts your professional credibility with AI.',
    setup: [
      { title: 'Create a LinkedIn account', desc: 'Go to linkedin.com and sign up with your name and email.' },
      { title: 'Complete your profile', desc: 'Add your headline (e.g. "Owner at Mile High Plumbing | Serving Denver, CO"), experience, and about section.' },
      { title: 'Upload a professional photo', desc: 'Use a clear, professional headshot.' },
      { title: 'Add your company page', desc: 'Go to linkedin.com/company/setup and create a page for your business.' },
    ],
    posting: [
      'Go to linkedin.com or open the app',
      'Click "Start a post" at the top of your feed',
      'Paste the content from GetChosenFirst',
      'Click "Post"',
    ],
    tips: [
      'LinkedIn posts about your expertise perform best',
      'Share industry insights and tips',
      'Engage with other local business owners\' posts',
      'Post 1-2 times per week',
    ],
  },
  'google-business': {
    icon: MapPin, color: '#EA4335', label: 'Google Business Profile',
    heroText: 'Google Business is the #1 signal AI uses when deciding who to mention.',
    setup: [
      { title: 'Go to business.google.com', desc: 'Click "Manage now" and sign in with your Google account.' },
      { title: 'Enter your business name', desc: 'Search for your business. If it doesn\'t exist, click "Add your business to Google."' },
      { title: 'Choose your category', desc: 'Pick the most specific category (e.g. "Plumber" not "Home Services").' },
      { title: 'Add your location', desc: 'Enter your address or service area.' },
      { title: 'Verify your business', desc: 'Google will send a verification code by mail, phone, or email.' },
      { title: 'Complete your profile', desc: 'Add hours, photos, services, and a business description.' },
    ],
    posting: [
      'Open the Google Maps app or go to business.google.com',
      'Tap your business name',
      'Tap "Add update" or "Create post"',
      'Select "Add update"',
      'Paste the content from GetChosenFirst',
      'Add a photo and tap "Post"',
    ],
    tips: [
      'Google Business Profile is critical — it\'s the #1 factor in local AI recommendations',
      'Post updates at least once a week',
      'Upload new photos regularly',
      'Always respond to reviews (we generate responses for you!)',
    ],
  },
  nextdoor: {
    icon: MapPin, color: '#8ED500', label: 'Nextdoor Business Page',
    heroText: 'Nextdoor connects you with neighbors who need your services.',
    setup: [
      { title: 'Go to nextdoor.com/create-business-page', desc: 'Click "Get started" and sign in or create an account.' },
      { title: 'Claim your business', desc: 'Search for your business name. If it exists, claim it. Otherwise, create a new listing.' },
      { title: 'Verify your business', desc: 'Nextdoor will verify you own the business via phone or email.' },
      { title: 'Complete your profile', desc: 'Add your business description, services, hours, and photos.' },
      { title: 'Add your service area', desc: 'Select the neighborhoods you serve.' },
    ],
    posting: [
      'Go to your Nextdoor Business Page',
      'Click "Create a post"',
      'Paste the content from GetChosenFirst',
      'Add a photo if you have one',
      'Select which neighborhoods to share with',
      'Click "Post"',
    ],
    tips: [
      'Nextdoor is hyper-local — focus on your immediate service area',
      'Respond to neighbor recommendations and questions',
      'Share helpful tips relevant to your neighborhood',
      'Offer special deals for Nextdoor neighbors',
      'Ask satisfied customers to recommend you on Nextdoor',
    ],
  },
  'x-twitter': {
    icon: Twitter, color: '#000000', label: 'X (Twitter)',
    heroText: 'X/Twitter is critical for Grok AI visibility. Every post feeds Grok.',
    setup: [
      { title: 'Create or claim your X account', desc: 'Go to x.com and sign up. Use your business name as the handle.' },
      { title: 'Complete your profile', desc: 'Add your business logo, cover photo, bio with city + profession, and website link.' },
      { title: 'Pin a welcome tweet', desc: 'Write a short intro: who you are, what you do, where you serve.' },
      { title: 'Follow local accounts', desc: 'Follow your city\'s official account, local news, and Chamber of Commerce.' },
      { title: 'Verify your account', desc: 'Consider X Premium for the blue check — it helps with AI visibility.' },
    ],
    posting: [
      'Go to x.com and click "Post"',
      'Paste the content from GetChosenFirst',
      'Keep it under 280 characters (or use a thread)',
      'Add 2-3 relevant hashtags (e.g. #DenverPlumber)',
      'Include a photo of your work if possible',
      'Click "Post"',
    ],
    tips: [
      'X/Twitter directly feeds Grok AI — every post matters',
      'Post 3-5 times per week for best Grok visibility',
      'Use location-specific hashtags (#YourCity + #YourProfession)',
      'Reply to local conversations and community posts',
      'Share before/after photos of your work',
      'Retweet positive customer mentions',
    ],
  },
  'bing-places': {
    icon: Globe, color: '#00897B', label: 'Bing Places',
    heroText: 'Bing Places powers Microsoft Copilot and Alexa recommendations.',
    setup: [
      { title: 'Go to bingplaces.com', desc: 'Click "Get Started" and sign in with your Microsoft account (or create one).' },
      { title: 'Import from Google', desc: 'If you have a Google Business Profile, choose "Import from Google" to auto-fill your data.' },
      { title: 'Or create manually', desc: 'Enter your business name, address, phone, category, and hours.' },
      { title: 'Verify your business', desc: 'Bing will verify via phone, SMS, or mail. Choose the fastest option.' },
      { title: 'Add photos and details', desc: 'Upload your best photos, add services, and write a description.' },
      { title: 'Link your website', desc: 'Confirm your website URL is added and correct.' },
    ],
    posting: [
      'Log in to bingplaces.com',
      'Click "Posts" in the sidebar',
      'Paste the content from GetChosenFirst',
      'Add a photo if available',
      'Click "Publish"',
    ],
    tips: [
      'Bing Places directly feeds Microsoft Copilot AI',
      'Import from Google Business Profile to save time',
      'Keep your info consistent across Google and Bing',
      'Add as many photos as possible — Copilot uses them',
      'Respond to Bing reviews if you receive any',
    ],
  },
};

export default function GuidePage() {
  const { platform } = useParams();
  const guide = GUIDES[platform];

  if (!guide) {
    return (
      <div className="min-h-screen bg-[#F7F4EF] flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-[#3C3226] font-medium">Guide not found</p>
          <Link to="/dashboard" className="text-sm text-[#1D6B4F] hover:underline mt-2 block">Back to Dashboard</Link>
        </div>
      </div>
    );
  }

  const Icon = guide.icon;

  return (
    <div className="min-h-screen bg-[#F7F4EF]" data-testid={`guide-page-${platform}`}>
      {/* Header */}
      <header className="px-6 pt-6 pb-4">
        <Link to="/content" className="inline-flex items-center gap-1.5 text-sm text-[#5C5347] hover:text-[#3C3226] mb-4" data-testid="guide-back-link">
          <ArrowLeft size={16} /> Back to Content
        </Link>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-none flex items-center justify-center" style={{ backgroundColor: `${guide.color}15` }}>
            <Icon size={24} style={{ color: guide.color }} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#3C3226]">{guide.label}</h1>
            <p className="text-sm text-[#5C5347]">{guide.heroText}</p>
          </div>
        </div>
      </header>

      <main className="px-6 max-w-lg mx-auto pb-12">
        {/* Setup section */}
        <section className="mb-8">
          <h2 className="text-sm font-bold uppercase tracking-[0.12em] text-[#C67A5C] mb-4">Setup (One Time)</h2>
          <div className="space-y-3">
            {guide.setup.map((step, idx) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="p-4 rounded-none bg-[#F7F4EF] border border-[#D4C9B8] flex gap-3"
              >
                <div className="w-7 h-7 rounded-none bg-[#3C3226] text-[#F0EBE3] text-xs font-bold flex items-center justify-center flex-shrink-0">{idx + 1}</div>
                <div>
                  <h3 className="text-sm font-semibold text-[#3C3226]">{step.title}</h3>
                  <p className="text-xs text-[#5C5347] mt-0.5 leading-relaxed">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* How to post section */}
        <section className="mb-8">
          <h2 className="text-sm font-bold uppercase tracking-[0.12em] text-[#1D6B4F] mb-4">How to Post</h2>
          <div className="p-5 rounded-none bg-[#F7F4EF] border border-[#D4C9B8]">
            <div className="space-y-3">
              {guide.posting.map((step, idx) => (
                <div key={step.slice(0, 20)} className="flex gap-3 items-start">
                  <div className="w-6 h-6 rounded-none bg-[#3C3226]/10 text-[#1D6B4F] text-xs font-bold flex items-center justify-center flex-shrink-0">{idx + 1}</div>
                  <p className="text-sm text-[#3C3226] leading-relaxed">{step}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Tips section */}
        <section>
          <h2 className="text-sm font-bold uppercase tracking-[0.12em] text-[#5C5347] mb-4">Pro Tips</h2>
          <div className="space-y-2">
            {guide.tips.map(tip => (
              <div key={tip.slice(0, 20)} className="flex items-start gap-2.5 p-3 rounded-sm bg-[#F7F4EF] border border-[#D4C9B8]">
                <CheckCircle2 size={14} className="text-[#1D6B4F] flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[#5C5347] leading-relaxed">{tip}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
